import { parseSourceInfoFromUrl } from '#src/app/shared/utils/petitionVideo';

export type VideoThumbnailOptions = {
	width?: number;
	height?: number;
	time?: string;
};

/**
 * Generic video type that only requires the videoUrl property.
 * This is compatible with various video types in the codebase (GraphQL results, SupporterVideo, etc.)
 */
type VideoWithUrl = {
	readonly videoUrl: string;
};

function getThumbnailQueryString(options?: VideoThumbnailOptions): string {
	if (!options?.width && !options?.height && !options?.time) {
		return '';
	}
	const params = new URLSearchParams();
	if (options.width) params.set('width', String(options.width));
	if (options.height) params.set('height', String(options.height));
	if (options.time) params.set('time', options.time);
	return params.toString();
}

/**
 * Extracts a thumbnail URL from a video URL.
 * For Mux videos, this generates a thumbnail URL using their image API (via parseSourceInfoFromUrl).
 * For other video sources, returns undefined (thumbnail not available without video element).
 *
 * @param videoUrl - The video URL to extract thumbnail from
 * @param options - Optional parameters for thumbnail customization (width, height, time offset)
 * @returns The thumbnail URL or undefined if not available
 */
export function getVideoThumbnail(videoUrl: string, options?: VideoThumbnailOptions): string | undefined {
	const sourceInfo = parseSourceInfoFromUrl(videoUrl);
	const thumbnailUrl = sourceInfo.type === 'mux' ? sourceInfo.poster : undefined;

	if (!thumbnailUrl) {
		return undefined;
	}

	const queryString = getThumbnailQueryString(options);
	if (!queryString) return thumbnailUrl;
	const separator = thumbnailUrl.includes('?') ? '&' : '?';
	return `${thumbnailUrl}${separator}${queryString}`;
}

/**
 * Filters supporter videos to get only those with valid video URLs.
 * Useful for getting video comments that can display thumbnails.
 *
 * @param videos - Array of videos with videoUrl property
 * @returns Array of videos with valid videoUrl
 */
export function filterVideoComments<T extends VideoWithUrl>(videos: readonly T[]): T[] {
	return videos.filter((video) => video.videoUrl && video.videoUrl.length > 0);
}

/**
 * Gets the first usable video thumbnail from an array of supporter videos.
 * Iterates in order and returns the thumbnail URL for the first video that yields
 * a thumbnail (e.g. Mux). This ensures mixed-source lists still
 * show the preview when a later video has a valid thumbnail.
 *
 * @param videos - Array of videos with videoUrl property
 * @param options - Optional parameters for thumbnail customization
 * @returns The first available thumbnail URL, or undefined if none of the videos support thumbnails
 */
export function getFirstVideoThumbnail(
	videos: readonly VideoWithUrl[],
	options?: VideoThumbnailOptions,
): string | undefined {
	const videoComments = filterVideoComments(videos);

	for (const video of videoComments) {
		const thumbnail = getVideoThumbnail(video.videoUrl, options);
		if (thumbnail) {
			return thumbnail;
		}
	}
	return undefined;
}

/**
 * Checks if there are any video comments available.
 *
 * @param videos - Array of videos with videoUrl property
 * @returns True if there is at least one video with a valid URL
 */
export function hasVideoComments(videos: readonly VideoWithUrl[]): boolean {
	return filterVideoComments(videos).length > 0;
}
